import { useState, useEffect } from 'react';
import { 
  Snackbar, 
  Alert, 
  Badge, 
  IconButton, 
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography
} from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import SyncIcon from '@mui/icons-material/Sync';
import { getPendingReports } from '../utils/offlineStorage';
import { syncOfflineReports } from '../utils/syncService';

/**
 * Component that shows offline status and pending submissions
 */
export default function OfflineNotification() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  const [showOnlineAlert, setShowOnlineAlert] = useState(false);
  const [pendingReports, setPendingReports] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [syncInProgress, setSyncInProgress] = useState(false);
  
  // Update online status and show alerts when status changes
  useEffect(() => {
    const updateOnlineStatus = async () => {
      const online = navigator.onLine;
      const wasOffline = !isOnline;
      setIsOnline(online);
      
      if (!online) {
        setShowOfflineAlert(true);
      } else if (wasOffline) {
        // Only show alert and trigger sync if we were offline before
        setShowOnlineAlert(true);
        
        // When coming back online, immediately trigger a sync with a slight delay
        // to ensure network is stable
        try {
          // Prevent multiple syncs from running at the same time
          if (syncInProgress) {
            console.log('Sync already in progress, skipping additional sync attempt');
            return;
          }
          
          setSyncInProgress(true);
          console.log('OfflineNotification: Device is back online - triggering sync');
          
          // Add a small delay before trying to sync
          await new Promise(resolve => setTimeout(resolve, 1500));
          const results = await syncOfflineReports();
          console.log('Auto-sync results:', results);
          
          if (results && results.length > 0) {
            setSyncResults({
              total: results.length,
              succeeded: results.filter(r => r.success).length,
              failed: results.filter(r => !r.success).length,
            });
          }
        } catch (error) {
          console.error('Error syncing reports on reconnect:', error);
        } finally {
          setSyncInProgress(false);
          await refreshPendingReports();
        }
      }
    };
    
    // Initial check - don't sync on initial load
    const initialCheck = async () => {
      setIsOnline(navigator.onLine);
      await refreshPendingReports();
    };
    
    initialCheck();
    
    // Listen for online/offline events
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, [isOnline, syncInProgress]);
  
  // Refresh pending reports
  const refreshPendingReports = async () => {
    try {
      const reports = await getPendingReports();
      setPendingReports(reports);
    } catch (error) {
      console.error('Error fetching pending reports:', error);
    }
  };
  
  // Open the pending reports dialog
  const handleBadgeClick = () => {
    refreshPendingReports();
    setDialogOpen(true);
  };
  
  // Close the pending reports dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  // Handle manual sync
  const handleSync = async () => {
    // Prevent sync if one is already in progress
    if (syncInProgress) {
      console.log('Sync already in progress, ignoring manual sync request');
      return;
    }
    
    try {
      setSyncing(true);
      setSyncInProgress(true);
      setSyncResults(null);
      
      console.log('Manual sync triggered by user');
      // Sync offline reports
      const results = await syncOfflineReports();
      console.log('Manual sync results:', results);
      
      // Update results and refresh pending reports
      if (results && results.length > 0) {
        setSyncResults({
          total: results.length,
          succeeded: results.filter(r => r.success).length,
          failed: results.filter(r => !r.success).length,
        });
      } else {
        setSyncResults({
          total: 0,
          succeeded: 0,
          failed: 0,
          message: 'No reports found to sync'
        });
      }
    } catch (error) {
      console.error('Error syncing reports:', error);
      setSyncResults({
        total: 0,
        succeeded: 0,
        failed: 0,
        error: error.message
      });
    } finally {
      setSyncing(false);
      setSyncInProgress(false);
      await refreshPendingReports();
    }
  };
  
  // Handle close of offline alert
  const handleCloseOfflineAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowOfflineAlert(false);
  };
  
  // Handle close of online alert
  const handleCloseOnlineAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowOnlineAlert(false);
  };
  
  return (
    <>
      {/* Offline indicator */}
      {!isOnline && (
        <Box position="fixed" bottom={16} right={16} zIndex={1000}>
          <Badge
            badgeContent={pendingReports.length}
            color="error"
            overlap="circular"
            onClick={handleBadgeClick}
          >
            <IconButton
              color="error"
              aria-label="offline"
              size="large"
              sx={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
            >
              <CloudOffIcon />
            </IconButton>
          </Badge>
        </Box>
      )}
      
      {/* Pending reports indicator when online */}
      {isOnline && pendingReports.length > 0 && (
        <Box position="fixed" bottom={16} right={16} zIndex={1000}>
          <Badge
            badgeContent={pendingReports.length}
            color="warning"
            overlap="circular"
            onClick={handleBadgeClick}
          >
            <IconButton
              color="primary"
              aria-label="pending reports"
              size="large"
              sx={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
            >
              <SyncIcon />
            </IconButton>
          </Badge>
        </Box>
      )}
      
      {/* Offline alert */}
      <Snackbar
        open={showOfflineAlert}
        autoHideDuration={6000}
        onClose={handleCloseOfflineAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseOfflineAlert} 
          severity="warning" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          You are offline. New reports will be saved locally and synced when you reconnect.
        </Alert>
      </Snackbar>
      
      {/* Online alert */}
      <Snackbar
        open={showOnlineAlert}
        autoHideDuration={4000}
        onClose={handleCloseOnlineAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseOnlineAlert} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          You are back online!
        </Alert>
      </Snackbar>
      
      {/* Pending reports dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isOnline ? 'Pending Reports' : 'Offline Mode - Pending Reports'}
        </DialogTitle>
        <DialogContent>
          {pendingReports.length === 0 ? (
            <Typography variant="body1" sx={{ py: 2 }}>
              No pending reports to sync.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {isOnline 
                  ? 'The following reports are waiting to be synced with the server:' 
                  : 'The following reports will be synced when you reconnect:'}
              </Typography>
              <List>
                {pendingReports.map((report) => (
                  <ListItem key={report.id} divider>
                    <ListItemText
                      primary={report.data?.description?.substring(0, 30) + '...' || 'Report'}
                      secondary={`Created: ${new Date(report.timestamp).toLocaleString()} - Status: ${report.status}`}
                    />
                  </ListItem>
                ))}
              </List>
              
              {syncResults && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="body2">
                    {syncResults.message ? (
                      syncResults.message
                    ) : syncResults.error ? (
                      <span style={{ color: 'red' }}>Error: {syncResults.error}</span>
                    ) : (
                      <>
                        <span style={{ fontWeight: 'bold', color: syncResults.succeeded > 0 ? 'green' : 'inherit' }}>
                          Sync results: {syncResults.succeeded} succeeded, {syncResults.failed} failed of {syncResults.total} total
                        </span>
                        {syncResults.succeeded > 0 && (
                          <Typography variant="body2" sx={{ mt: 1, color: 'green' }}>
                            ✓ Your offline data has been successfully sent to the server!
                          </Typography>
                        )}
                      </>
                    )}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {isOnline && pendingReports.length > 0 && (
            <Button 
              onClick={handleSync} 
              color="primary" 
              startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          )}
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
} 